import { useEffect, useState } from 'react'

import { useDispatch } from 'react-redux'

import { LINGERS_LOCAL_STORAGE_KEY } from 'app/constants/localStorageKeys'
import { dispatchFormAndMessage } from 'app/redux/messages/messages.actions'
import { playNotifSound } from 'app/utils/sound'

// lingers are in this format ---> departmentId_lingerId_timeoutId
function useLinger(messagesData, lingers, aiBotEnabled) {
  // stores lingers that apply for each department. the key is department id.
  const [queuedLingersTimeout, setQueuedLingersTimout] = useState([])
  const dispatch = useDispatch()

  const departmentsData = messagesData?.departments?.data
  const departmentsWithAcceptedChats = Object.keys(messagesData.acceptedChat)

  const saveLingerHistoryInLocalStorage = (queueId) => {
    const prevSavedLingers = JSON.parse(localStorage.getItem(LINGERS_LOCAL_STORAGE_KEY)) || []
    const newArray = [...prevSavedLingers, queueId]

    localStorage.setItem(LINGERS_LOCAL_STORAGE_KEY, JSON.stringify(newArray))
  }

  const isLingerAlreadySavedInLocalStorage = (queueId) => {
    return JSON.parse(localStorage.getItem(LINGERS_LOCAL_STORAGE_KEY))?.includes(queueId)
  }

  const clearQueueIdFromLocalStorage = (id) => {
    const savedLingresString = localStorage.getItem(LINGERS_LOCAL_STORAGE_KEY)

    if (!savedLingresString) return

    const newLingers = JSON.parse(savedLingresString).filter((item) => item !== id)

    localStorage.setItem(LINGERS_LOCAL_STORAGE_KEY, JSON.stringify(newLingers))
  }

  //   puts a linger to the queue by setting a timeout for it
  const queueLinger = (linger, delay, departmentId) => {
    const queueIdString = `${departmentId}_${linger.index}`

    if (isLingerAlreadySavedInLocalStorage(queueIdString)) {
      return
    }

    // do not set timeout for this linger if one is already set
    if (queuedLingersTimeout.some((item) => item.startsWith(queueIdString))) return

    const timeoutId = setTimeout(function () {
      if (linger.sound) {
        playNotifSound()
      }

      dispatch(
        dispatchFormAndMessage({
          message: linger.message,
          formFields: linger.forms,
          formTitle: linger.title,
          teamId: departmentId,
          formId: new Date().getTime(),
          showMessage: true,
        }),
      )

      saveLingerHistoryInLocalStorage(queueIdString)
    }, delay)

    setQueuedLingersTimout((prev) => [...prev, `${queueIdString}_${timeoutId}`])
  }

  // clears timeout that is set for a linger
  const deQueueLinger = (departmentId) => {
    const getNewQueueLingerList = (list) =>
      list.filter((item) => {
        const [id, lingerId, timeoutId] = item.split('_')

        if (id === departmentId) {
          clearQueueIdFromLocalStorage(`${departmentId}_${lingerId}`)
          clearTimeout(timeoutId)
          return false
        }

        return true
      })

    setQueuedLingersTimout((prev) => getNewQueueLingerList(prev))
  }

  const getLingersSetToDepartment = (id) => {
    return lingers.filter((item) => item.teamsIds.includes(id) || item.isAllTeams)
  }

  // auto messages, forms and bot messages are not counted as last messages because are not sent by user
  const getLastHumanWrittenMessage = (messages) =>
    messages
      .slice()
      .reverse()
      .find((item) => !item.isAutoMessage && item.type !== 'dynamicForm' && !item.botId)

  const isDepartmentLastConvFromUser = (departmentId) => {
    const department = departmentsData.find((department) => department._id === departmentId)

    const departmentMessages = department?.messages
    const lastMessage = getLastHumanWrittenMessage(departmentMessages)

    const isUserWaiting = !!lastMessage?.me

    return [isUserWaiting, lastMessage?.createdAt]
  }

  const doesConversationTypeConditionPass = (lingerType, isAccepted) => {
    return (lingerType === 2 && isAccepted) || (lingerType === 1 && !isAccepted)
  }

  const getLingerTimeInMs = (linger) => {
    const time = linger.condition

    return linger.conditionType === 'minute' ? time * 60000 : time * 1000
  }

  const checkIfLingerShouldFireAndQueueAction = (lingers, departmentId) => {
    const [isUserInWaitingMode, lastMessageTime] = isDepartmentLastConvFromUser(departmentId)
    const isDepartmentInAcceptedMode = departmentsWithAcceptedChats.includes(departmentId)

    // de-quaue all lingers assigned to this department that are already queued
    if (!isUserInWaitingMode) {
      deQueueLinger(departmentId)
      return
    }

    for (let linger of lingers) {
      const conversationTypePass = doesConversationTypeConditionPass(linger.type, isDepartmentInAcceptedMode)

      if (conversationTypePass) {
        const timeoutDelay = new Date(lastMessageTime).getTime() + getLingerTimeInMs(linger) - new Date().getTime()

        // There is a situation that timeoutDelay is negative which means that the time which linger should fire is passed therefore it must fire immmediately.
        const flooredTimeout = Math.max(timeoutDelay, 0)

        queueLinger(linger, flooredTimeout, departmentId)
      }
    }
  }

  const getLingersAssignedToDepartments = () => {
    const lingersAssigned = {}

    for (let department of departmentsData) {
      const departmentId = department._id

      const departmentLingers = getLingersSetToDepartment(departmentId)

      checkIfLingerShouldFireAndQueueAction(departmentLingers, departmentId)
    }

    return lingersAssigned
  }

  useEffect(() => {
    if (!aiBotEnabled) {
      getLingersAssignedToDepartments()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentsData])
}

export default useLinger
